import { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../services/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';

interface Invite {
  id: string;
  groupId: string;
  groupName: string;
  invitedByEmail?: string;
  invitedByName?: string;
  welcomeMessage?: string;
  groupDescription?: string;
  groupImageUrl?: string | null;
  createdAt: string;
}

interface MembershipRequest {
  id: string;
  groupId: string;
  groupName: string;
  userId: string;
  userEmail: string;
  userName: string;
  status: 'pending' | 'approved' | 'rejected';
  message?: string;
  createdAt: string;
  resolvedViaInvitation?: boolean;
  viewedByUserAt?: string;
}

interface GroupWithRequests {
  groupId: string;
  groupName: string;
  requests: MembershipRequest[];
}

export default function GroupNotificationsPage() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [myRequests, setMyRequests] = useState<MembershipRequest[]>([]);
  const [groupRequests, setGroupRequests] = useState<GroupWithRequests[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Show toast helper
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Mark approved request as viewed on server
  const markApprovedRequestAsViewed = async (requestId: string) => {
    try {
      await apiPost(`/membership-requests/${requestId}/mark-viewed`, {});
      // Invalidate badge queries to update counts
      queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
      queryClient.invalidateQueries({ queryKey: ['approved-requests-count'] });
      // Refresh notifications to get updated viewedByUserAt
      await fetchNotifications();
    } catch (err) {
      console.error('Failed to mark request as viewed', err);
    }
  };

  // Handle click on approved request
  const handleApprovedRequestClick = async (groupId: string, requestId: string) => {
    await markApprovedRequestAsViewed(requestId);
    navigate(`/app/groups/${groupId}`);
  };

  async function fetchNotifications() {
    setLoading(true);
    try {
      // Fetch invites (from other users)
      const invitesRes = await apiGet<Invite[]>('/invites');
      if (invitesRes.success && invitesRes.data) {
        setInvites(invitesRes.data as Invite[]);
      }

      // Fetch my membership requests
      const myRequestsRes = await apiGet<{ requests: MembershipRequest[] }>('/membership-requests/my-requests');
      if (myRequestsRes.success && myRequestsRes.data) {
        setMyRequests(myRequestsRes.data.requests || []);
      }

      // Fetch membership requests for groups I own
      // First, get all my groups
      const groupsRes = await apiGet<any[]>('/groups');
      if (groupsRes.success && groupsRes.data) {
        const myGroups = groupsRes.data || [];
        const ownedGroups = myGroups.filter((g: any) => {
          // Check if I'm an owner
          const members = g.members || [];
          return members.some((m: any) => 
            (m.userId === user?.id || m.id === user?.id) && m.role === 'owner'
          );
        });

        // Fetch requests for each owned group
        const requestsPromises = ownedGroups.map(async (group: any) => {
          const res = await apiGet<{ requests: MembershipRequest[] }>(`/groups/${group.id}/membership-requests`);
          if (res.success && res.data && res.data.requests.length > 0) {
            return {
              groupId: group.id,
              groupName: group.name,
              requests: res.data.requests,
            };
          }
          return null;
        });

        const results = await Promise.all(requestsPromises);
        const validResults = results.filter((r): r is GroupWithRequests => r !== null);
        setGroupRequests(validResults);
      }
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchNotifications();
    
    // Invalidate unread counts to update badges in Layout when viewing notifications
    queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
     
  }, [user?.id]);

  async function handleAccept(inviteId: string) {
    // Find the invite to get the groupId
    const invite = invites.find(inv => inv.id === inviteId);
    if (!invite) return;

    // Premium check: free users can only be members of max 2 groups
    if (user?.subscriptionTier !== 'premium') {
      try {
        const res = await apiGet<{ groups: Array<{ id: string }> }>('/groups/my-groups');
        if (res.success && res.data) {
          const memberGroupCount = res.data.groups.length;
          if (memberGroupCount >= 2) {
            setShowPremiumModal(true);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check group membership count', err);
        // Continue with accept if check fails
      }
    }
    
    setActionLoading(inviteId);
    try {
      await apiPost(`/invites/${inviteId}/accept`, {});
      setInvites((s) => s.filter((g) => g.id !== inviteId));
      // Invalidate all badge-related queries
      try { 
        queryClient.invalidateQueries({ queryKey: ['invites-summary'] });
        queryClient.invalidateQueries({ queryKey: ['invites-summary-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['pending-invites-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
        queryClient.invalidateQueries({ queryKey: ['approved-requests-count'] });
      } catch (e) { void e; }
      
      // Navigate to the group page
      navigate(`/app/groups/${invite.groupId}`);
    } catch (err) {
      console.error('Accept failed', err);
      showToast('Misslyckades att gå med i grupp', 'error');
      setActionLoading(null);
    }
  }

  async function handleReject(inviteId: string) {
    setActionLoading(inviteId);
    try {
      await apiPost(`/invites/${inviteId}/reject`, {});
      setInvites((s) => s.filter((g) => g.id !== inviteId));
      // Invalidate all badge-related queries
      try { 
        queryClient.invalidateQueries({ queryKey: ['invites-summary'] });
        queryClient.invalidateQueries({ queryKey: ['invites-summary-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['pending-invites-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
        queryClient.invalidateQueries({ queryKey: ['approved-requests-count'] });
      } catch (e) { void e; }
    } catch (err) {
      console.error('Reject failed', err);
      showToast('Misslyckades att neka inbjudan', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleApproveMembershipRequest(groupId: string, requestId: string) {
    setActionLoading(requestId);
    try {
      await apiPost(`/groups/${groupId}/membership-requests/${requestId}/approve`, {});
      // Remove from local state
      setGroupRequests(prev => prev.map(gr => {
        if (gr.groupId === groupId) {
          return {
            ...gr,
            requests: gr.requests.filter(r => r.id !== requestId)
          };
        }
        return gr;
      }).filter(gr => gr.requests.length > 0));
      
      // Invalidate badge queries
      try {
        queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
        queryClient.invalidateQueries({ queryKey: ['approved-requests-count'] });
      } catch (e) {
        void e;
      }
      
      showToast('Medlemskap godkänt!');
    } catch (err) {
      console.error('Approve failed', err);
      showToast('Misslyckades att godkänna ansökan', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRejectMembershipRequest(groupId: string, requestId: string) {
    setActionLoading(requestId);
    try {
      await apiPost(`/groups/${groupId}/membership-requests/${requestId}/reject`, {});
      // Remove from local state
      setGroupRequests(prev => prev.map(gr => {
        if (gr.groupId === groupId) {
          return {
            ...gr,
            requests: gr.requests.filter(r => r.id !== requestId)
          };
        }
        return gr;
      }).filter(gr => gr.requests.length > 0));
      
      // Invalidate badge queries
      try {
        queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
        queryClient.invalidateQueries({ queryKey: ['approved-requests-count'] });
      } catch (e) {
        void e;
      }
      
      showToast('Ansökan nekad');
    } catch (err) {
      console.error('Reject failed', err);
      showToast('Misslyckades att neka ansökan', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  const totalNotifications = invites.length + groupRequests.reduce((sum, gr) => sum + gr.requests.length, 0) + myRequests.length;

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div 
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-top-5 ${
            toast.type === 'success' 
              ? 'bg-green-900/90 text-green-100 border border-green-700' 
              : 'bg-red-900/90 text-red-100 border border-red-700'
          }`}
        >
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )}
      
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquare className="w-7 h-7" />
        <h1 className="text-2xl font-semibold">Notiser</h1>
      </div>
      
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-4 animate-pulse"
            >
              <div className="flex items-start gap-3">
                {/* Image skeleton */}
                <div className="rounded-lg bg-muted w-16 h-16 shrink-0" />
                
                <div className="flex-1 min-w-0 space-y-3">
                  {/* Title skeleton */}
                  <div className="h-5 bg-muted rounded w-2/3" />
                  {/* Subtitle skeleton */}
                  <div className="h-4 bg-muted rounded w-1/2" />
                  {/* Message skeleton */}
                  <div className="h-16 bg-muted rounded w-full" />
                  {/* Buttons skeleton */}
                  <div className="flex gap-2">
                    <div className="h-8 bg-muted rounded w-20" />
                    <div className="h-8 bg-muted rounded w-24" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : totalNotifications === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <svg 
              className="w-10 h-10 text-muted-foreground/50" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
              />
            </svg>
          </div>
          <p className="text-lg font-medium text-muted-foreground mb-1">Inga notiser just nu</p>
          <p className="text-sm text-muted-foreground/70 text-center max-w-sm">
            Du får notiser här när du blir inbjuden till grupper eller när någon ansöker om medlemskap i dina grupper.
          </p>
        </div>
      ) : (
        <>
          {/* Invitations to groups */}
          {invites.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium">Inbjudningar till grupper</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Du har blivit inbjuden till följande grupper.
              </p>
              {invites.map((inv) => {
                const inviterDisplay = inv.invitedByName || inv.invitedByEmail || 'Okänd';
                const inviteDate = inv.createdAt ? new Date(inv.createdAt).toLocaleString('sv-SE', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : '';
                
                return (
                <div key={inv.id} className="p-4 border rounded-lg bg-card space-y-5">
                  {/* Header with group image and name */}
                  <div className="flex items-start gap-3">
                    {inv.groupImageUrl && (
                      <img 
                        src={inv.groupImageUrl} 
                        alt={inv.groupName}
                        className="w-16 h-16 rounded-lg object-cover shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium break-words">{inv.groupName}</h3>
                      <p className="text-sm text-muted-foreground break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>Inbjuden av: {inviterDisplay}</p>
                      {inviteDate && (
                        <p className="text-xs text-muted-foreground mt-1">{inviteDate}</p>
                      )}
                    </div>
                  </div>

                  {/* Welcome message */}
                  {inv.welcomeMessage && (
                    <div className="p-3 rounded-lg bg-muted/50 border border-border">
                      <p className="text-sm text-muted-foreground break-words">{inv.welcomeMessage}</p>
                    </div>
                  )}

                  {/* Group description */}
                  {inv.groupDescription && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">Om gruppen</h4>
                      <p className="text-sm text-muted-foreground break-words">{inv.groupDescription}</p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 rounded border border-border hover:bg-muted"
                      onClick={() => handleReject(inv.id)}
                      disabled={actionLoading === inv.id}
                    >
                      {actionLoading === inv.id ? '...' : 'Neka'}
                    </button>
                    <button
                      className="px-3 py-1 rounded text-white hover:opacity-90"
                      style={{ backgroundColor: '#67724e' }}
                      onClick={() => handleAccept(inv.id)}
                      disabled={actionLoading === inv.id}
                    >
                      {actionLoading === inv.id ? '...' : 'Gå med'}
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Membership requests for groups I own */}
          {groupRequests.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium">Ansökningar om medlemskap</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Följande personer vill gå med i dina grupper.
              </p>
              {groupRequests.map((groupReq) => (
                <div key={groupReq.groupId} className="space-y-2">
                  <h3 className="font-medium text-base break-words">{groupReq.groupName}</h3>
                  {groupReq.requests.map((req) => (
                    <div key={req.id} className="p-4 rounded-lg bg-card ml-4" style={{ border: '2px solid #945123' }}>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>{req.userName}</h4>
                        <p className="text-sm text-muted-foreground break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>{req.userEmail}</p>
                        {req.message && (
                          <p className="text-sm mt-2 italic text-muted-foreground break-words">
                            &quot;{req.message}&quot;
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Ansökt {new Date(req.createdAt).toLocaleDateString('sv-SE')}
                        </p>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          className="px-3 py-1 rounded border border-border hover:bg-muted"
                          onClick={() => handleRejectMembershipRequest(groupReq.groupId, req.id)}
                          disabled={actionLoading === req.id}
                        >
                          {actionLoading === req.id ? '...' : 'Neka'}
                        </button>
                        <button
                          className="px-3 py-1 rounded text-white hover:opacity-90"
                          style={{ backgroundColor: '#67724e' }}
                          onClick={() => handleApproveMembershipRequest(groupReq.groupId, req.id)}
                          disabled={actionLoading === req.id}
                        >
                          {actionLoading === req.id ? '...' : 'Godkänn'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* My membership requests status - always shown */}
          <div className="space-y-3">
            <h2 className="text-lg font-medium">Mina ansökningar</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Status på dina ansökningar om medlemskap.
            </p>
            {(() => {
              // Filter requests: show all if less than 60 days old, or if approved/rejected and unviewed
              const now = new Date();
              const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
              
              const filteredRequests = myRequests.filter(req => {
                const createdAt = new Date(req.createdAt);
                const isRecent = createdAt > sixtyDaysAgo;
                const isUnviewed = (req.status === 'approved' || req.status === 'rejected') && !req.resolvedViaInvitation && !req.viewedByUserAt;
                return isRecent || isUnviewed;
              });
              
              return filteredRequests.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Du har inga ansökningar.</p>
              ) : (
                filteredRequests.map((req) => {
                  const isApproved = req.status === 'approved';
                  const isPending = req.status === 'pending';
                  const isUnviewed = (isApproved || req.status === 'rejected') && !req.resolvedViaInvitation && !req.viewedByUserAt;
                  const canOpenGroupApplicationPage = isPending || (isApproved && !req.resolvedViaInvitation);
                  
                  return (
                <div 
                  key={req.id} 
                  className={`p-4 border rounded-lg bg-card ${
                    canOpenGroupApplicationPage ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''
                  } ${
                    isUnviewed ? 'ring-2' : ''
                  }`}
                  style={isUnviewed ? { borderColor: '#945123', '--tw-ring-color': '#945123' } as React.CSSProperties : {}}
                  onClick={() => {
                    if (isPending) {
                      navigate(`/groups/${req.groupId}`);
                      return;
                    }
                    if (isApproved && !req.resolvedViaInvitation) {
                      void handleApprovedRequestClick(req.groupId, req.id);
                    }
                  }}
                >
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>{req.groupName}</h3>
                        {isUnviewed && (
                          <span className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ backgroundColor: '#945123' }} title="Ny godkänd ansökan" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Ansökt {new Date(req.createdAt).toLocaleDateString('sv-SE')}
                      </p>
                      {req.resolvedViaInvitation && (
                        <p className="text-xs text-muted-foreground/70 mt-1">
                          Gick med via inbjudan
                        </p>
                      )}
                    </div>
                    <div className="shrink-0">
                      {req.status === 'pending' && (
                        <span className="px-3 py-1 rounded-full bg-yellow-900/30 text-yellow-300 text-sm">
                          Väntar på svar
                        </span>
                      )}
                      {req.status === 'approved' && !req.resolvedViaInvitation && (
                        <span className="px-3 py-1 rounded-full bg-green-900/30 text-green-300 text-sm">
                          Godkänd!
                        </span>
                      )}
                      {req.resolvedViaInvitation && (
                        <span className="px-3 py-1 rounded-full bg-blue-900/30 text-blue-300 text-sm">
                          Via inbjudan
                        </span>
                      )}
                      {req.status === 'rejected' && (
                        <span className="px-3 py-1 rounded-full bg-red-900/30 text-red-300 text-sm">
                          Nekad
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                  );
                })
              );
            })()}
          </div>
        </>
      )}
    </div>

    {/* Premium modal */}
    <PremiumRequiredModal
      open={showPremiumModal}
      onClose={() => setShowPremiumModal(false)}
      feature="Vara medlem i fler än 2 beredskapsgrupper"
    />
    </>
  );
}

